import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, PlayCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Admin-only "Run Migration" cards for the two production data migrations.
// Every action is preview-first: the user opens a dialog, sees the exact
// dry-run plan, then clicks "Apply" to actually mutate.

export default function TierMigrationsCard({ API }) {
  return (
    <Card data-testid="tier-migrations-card">
      <CardHeader>
        <CardTitle>Tier & Hierarchy Migrations</CardTitle>
        <CardDescription>
          Preview-first bulk operations for the distribution chain. Every action
          runs a dry-run first — nothing is written until you confirm.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RunAllMigrations API={API} />
        <RevertCard API={API} />
        <div className="grid gap-4 md:grid-cols-2">
          <ReclassifyPartnersToLco API={API} />
          <PruneSubDistributors API={API} />
          <AssignUnassignedLcos API={API} />
        </div>
      </CardContent>
    </Card>
  );
}

function RunAllMigrations({ API }) {
  const [open, setOpen] = useState(false);
  const [keep, setKeep] = useState('ANT-P2026033178,ANT-P2026040653,ANT-P2026062478,ANT-B2026055168');
  const [target, setTarget] = useState('ANT-P2026040653');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const openDialog = async () => {
    setConfirmText('');
    setOpen(true);
    await runDryRun();
  };

  const runDryRun = async () => {
    setLoading(true);
    setPlan(null);
    try {
      const r = await axios.post(`${API}/admin/migrations/run-all`, null, {
        params: { keep_codes: keep, target_code: target, dry_run: true },
      });
      setPlan(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/migrations/run-all`, null, {
        params: { keep_codes: keep, target_code: target, dry_run: false },
      });
      const s = r.data.summary;
      toast.success(
        `Done — reclassified ${s.reclassified_to_lco}, promoted ${s.promoted_to_sub}, demoted ${s.demoted_to_lco}, attached ${s.lcos_attached} LCO(s).`
      );
      setConfirmText('');
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setApplying(false);
    }
  };

  const hasAnyWork =
    plan &&
    (plan.summary.reclassified_to_lco + plan.summary.promoted_to_sub +
     plan.summary.demoted_to_lco + plan.summary.lcos_attached) > 0;

  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-indigo-700" />
          <h4 className="font-semibold text-sm">Run all 3 migrations in sequence</h4>
        </div>
        <p className="text-xs text-slate-600 mt-1">
          Reclassify Partners → LCO · Prune Sub Distributors · Assign Unassigned LCOs — one preview, one apply.
        </p>
      </div>
      <Button size="sm" onClick={openDialog} data-testid="btn-run-all-migrations" className="bg-indigo-600 hover:bg-indigo-700">
        Preview & Run All
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Run all 3 Tier Migrations</DialogTitle>
            <DialogDescription>
              All three migrations chained in the correct order. Preview the
              aggregate plan, then click <b>Apply</b> to run everything.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="runall-keep">Keep as Sub Distributor</Label>
                <Input
                  id="runall-keep"
                  value={keep}
                  onChange={(e) => setKeep(e.target.value)}
                  onBlur={runDryRun}
                  data-testid="runall-keep-codes"
                />
              </div>
              <div>
                <Label htmlFor="runall-target">Assign orphan LCOs to</Label>
                <Input
                  id="runall-target"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  onBlur={runDryRun}
                  data-testid="runall-target"
                />
              </div>
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading plan…
              </div>
            )}
            {plan && (
              <div className="rounded border bg-white p-3 space-y-1 text-sm">
                <p>Step 1 — reclassify partners → LCO: <b>{plan.summary.reclassified_to_lco}</b> row(s)</p>
                <p>Step 2 — prune sub distributors: promote <b>{plan.summary.promoted_to_sub}</b>, demote <b>{plan.summary.demoted_to_lco}</b></p>
                <p>Step 3 — attach orphan LCOs to <b>{target}</b>: <b>{plan.summary.lcos_attached}</b> LCO(s)</p>
                {!hasAnyWork && (
                  <p className="text-xs text-emerald-700">Everything is already up to date — nothing to apply.</p>
                )}
              </div>
            )}
            {hasAnyWork && (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2">
                <div className="flex items-start gap-2 text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p className="text-xs">
                    This mutates up to{' '}
                    <b>
                      {plan.summary.reclassified_to_lco + plan.summary.promoted_to_sub +
                       plan.summary.demoted_to_lco + plan.summary.lcos_attached}
                    </b>{' '}
                    customer row(s). Type <code className="px-1 bg-white border rounded">APPLY</code> below to enable the Apply button.
                  </p>
                </div>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type APPLY to confirm"
                  data-testid="runall-confirm-text"
                  className="bg-white"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={runDryRun} disabled={loading}>
              Refresh preview
            </Button>
            <Button
              onClick={apply}
              disabled={applying || loading || !hasAnyWork || confirmText.trim() !== 'APPLY'}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="runall-apply-btn"
            >
              {applying ? 'Applying…' : 'Apply all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== Revert Card ===============================================
function RevertCard({ API }) {
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [heuristicOpen, setHeuristicOpen] = useState(false);
  const [heuristicPlan, setHeuristicPlan] = useState(null);
  const [heuristicLoading, setHeuristicLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/migrations/snapshots`);
      setSnapshots(r.data.snapshots || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load snapshots');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async () => { setOpen(true); await refresh(); };

  const revert = async (batch_id) => {
    if (!window.confirm('Revert this migration? Every mutated row will be restored to its pre-migration state.')) return;
    try {
      const r = await axios.post(`${API}/admin/migrations/revert/${batch_id}`);
      toast.success(`Reverted — restored ${r.data.restored} row(s).`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    }
  };

  const openHeuristic = async () => {
    setHeuristicOpen(true);
    setHeuristicPlan(null);
    setHeuristicLoading(true);
    try {
      const r = await axios.post(`${API}/admin/migrations/heuristic-revert-reclassify`, null, {
        params: { dry_run: true },
      });
      setHeuristicPlan(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setHeuristicLoading(false);
    }
  };

  const applyHeuristic = async () => {
    if (!window.confirm('Restore every unlinked LCO back to Master Distributor? This is a heuristic revert — review the preview list first.')) return;
    try {
      const r = await axios.post(`${API}/admin/migrations/heuristic-revert-reclassify`, null, {
        params: { dry_run: false },
      });
      toast.success(`Restored ${r.data.applied_count} row(s) → Master Distributor.`);
      setHeuristicOpen(false);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revert failed');
    }
  };

  return (
    <div className="rounded-md border border-rose-200 bg-rose-50/60 p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-rose-700" />
          <h4 className="font-semibold text-sm">Revert tier migrations</h4>
        </div>
        <p className="text-xs text-slate-600 mt-1">
          Restore the exact pre-migration state (snapshot) — or if the migration ran before snapshots existed, use the heuristic to promote back any LCO with no parent links.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={openHeuristic} data-testid="btn-heuristic-revert">
          Heuristic revert
        </Button>
        <Button size="sm" onClick={openDialog} data-testid="btn-open-snapshots" className="bg-rose-600 hover:bg-rose-700">
          Snapshots
        </Button>
      </div>

      {/* Snapshots dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Migration Snapshots</DialogTitle>
            <DialogDescription>
              Every successful migration captures the pre-change state of each
              row. Click <b>Revert</b> on any snapshot to roll it back.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {!loading && snapshots.length === 0 && (
              <p className="text-sm text-slate-500">No snapshots yet. New migrations will show up here.</p>
            )}
            {snapshots.map((s) => (
              <div key={s.batch_id} className="flex items-center justify-between gap-2 rounded border bg-white px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-mono text-xs">{s.batch_id.slice(0, 8)} · {s.migration}</p>
                  <p className="text-xs text-slate-500">{new Date(s.created_at).toLocaleString()} · {s.row_count} rows · by {s.actor}</p>
                  {s.reverted_at && (
                    <p className="text-xs text-emerald-700">Reverted at {new Date(s.reverted_at).toLocaleString()}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={s.reverted_at ? 'outline' : 'default'}
                  disabled={!!s.reverted_at}
                  onClick={() => revert(s.batch_id)}
                  data-testid={`revert-${s.batch_id}`}
                  className={s.reverted_at ? '' : 'bg-rose-600 hover:bg-rose-700'}
                >
                  {s.reverted_at ? 'Already reverted' : 'Revert'}
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={refresh} disabled={loading}>Refresh</Button>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Heuristic revert dialog */}
      <Dialog open={heuristicOpen} onOpenChange={setHeuristicOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Heuristic Revert — Reclassify Partners</DialogTitle>
            <DialogDescription>
              Use when a migration ran <b>before snapshots existed</b>. Every
              <code> customer_type='lco'</code> row with NO <code>parent_partner_id</code> is
              assumed to be a demoted standalone partner and promoted back to
              Master Distributor. Any stale <code>parent_distributor_id</code> on
              those rows is also cleared.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {heuristicLoading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}
            {heuristicPlan && (
              <div className="rounded border bg-white p-3 text-sm space-y-2 max-h-72 overflow-auto">
                <p><b>Candidates to restore:</b> {heuristicPlan.totals?.restore_to_partner}</p>
                {heuristicPlan.candidates?.length > 0 && (
                  <ul className="ml-3 list-disc text-xs text-slate-600 space-y-0.5">
                    {heuristicPlan.candidates.slice(0, 30).map((c) => (
                      <li key={c.customer_id}>{c.customer_code || c.customer_id} — {c.company_name}</li>
                    ))}
                    {heuristicPlan.candidates.length > 30 && (
                      <li className="italic">…and {heuristicPlan.candidates.length - 30} more previewed</li>
                    )}
                  </ul>
                )}
                {heuristicPlan.truncated_preview && (
                  <p className="text-xs text-slate-500">(preview truncated to first 250)</p>
                )}
              </div>
            )}
            <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 text-xs">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>This is a <b>heuristic</b> — it can mistakenly promote a legitimate standalone LCO. Review the list carefully before applying.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHeuristicOpen(false)}>Cancel</Button>
            <Button
              onClick={applyHeuristic}
              disabled={heuristicLoading || !heuristicPlan || (heuristicPlan?.totals?.restore_to_partner || 0) === 0}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="heuristic-apply-btn"
            >
              Restore to Master Distributor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function ReclassifyPartnersToLco({ API }) {
  const [open, setOpen] = useState(false);
  const [keep, setKeep] = useState('ANT-P2026033178,ANT-P2026040653,ANT-P2026062478,ANT-B2026055168');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    await runDryRun(keep);
  };

  const runDryRun = async (codes) => {
    setLoading(true);
    setPlan(null);
    try {
      const r = await axios.post(`${API}/admin/migrations/reclassify-partners-to-lco`, null, {
        params: { keep_codes: codes, dry_run: true },
      });
      setPlan(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/migrations/reclassify-partners-to-lco`, null, {
        params: { keep_codes: keep, dry_run: false },
      });
      toast.success(`Reclassified ${r.data.applied_count || r.data.totals.demote} partner(s) → LCO`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-md border p-4 bg-slate-50 space-y-2">
      <div className="flex items-center gap-2">
        <PlayCircle className="h-4 w-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Reclassify Partners → LCO</h4>
      </div>
      <p className="text-xs text-slate-600">
        Any <code>customer_type='partner'</code> row WITHOUT{' '}
        <code>lco_management_enabled=True</code> gets demoted to <code>lco</code>.
        Only true Master Distributors stay. Use <b>Keep codes</b> to protect
        extra Master Distributors that don't yet have the flag.
      </p>
      <Button size="sm" onClick={openDialog} data-testid="btn-reclassify-partners">
        Preview & Run
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reclassify Partners → LCO</DialogTitle>
            <DialogDescription>
              Preview the exact rows that will change, then click <b>Apply</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="reclassify-keep-codes">
                Keep as Master Distributor (customer codes, comma-separated)
              </Label>
              <Input
                id="reclassify-keep-codes"
                value={keep}
                onChange={(e) => setKeep(e.target.value)}
                onBlur={() => runDryRun(keep)}
                data-testid="reclassify-keep-codes"
              />
              <p className="text-xs text-slate-500 mt-1">
                These codes will stay as Master Distributor even if their
                <code> lco_management_enabled</code> flag is missing.
              </p>
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}
            {plan && (
              <div className="rounded border bg-white text-sm p-3 space-y-2 max-h-72 overflow-auto">
                <p>
                  <b>Will remain Master Distributor:</b>{' '}
                  <span className="text-emerald-700">{plan.totals.will_remain_partner}</span>
                </p>
                <p>
                  <b>Will demote to LCO:</b>{' '}
                  <span className="text-amber-700">{plan.totals.demote}</span>
                  {plan.truncated_preview && (
                    <span className="text-xs text-slate-500"> (preview truncated to first 250)</span>
                  )}
                </p>
                {plan.protected_resolved?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500">Protected (will stay partner):</p>
                    <ul className="ml-3 list-disc text-xs text-slate-600">
                      {plan.protected_resolved.map((r) => (
                        <li key={r.customer_code}>{r.customer_code} — {r.company_name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {plan.will_demote_to_lco?.length > 0 && (
                  <ul className="ml-3 list-disc text-xs text-slate-600 space-y-0.5">
                    {plan.will_demote_to_lco.slice(0, 30).map((r) => (
                      <li key={r.customer_id}>{r.customer_code} — {r.company_name}</li>
                    ))}
                    {plan.will_demote_to_lco.length > 30 && (
                      <li className="italic">…and {plan.will_demote_to_lco.length - 30} more previewed</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => runDryRun(keep)} disabled={loading}>
              Refresh preview
            </Button>
            <Button
              onClick={apply}
              disabled={applying || loading || !plan || (plan.totals?.demote || 0) === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reclassify-apply-btn"
            >
              {applying ? 'Applying…' : 'Apply migration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PruneSubDistributors({ API }) {
  const [open, setOpen] = useState(false);
  const [keep, setKeep] = useState('ANT-P2026033178,ANT-P2026040653,ANT-P2026062478,ANT-B2026055168');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    await runDryRun(keep);
  };

  const runDryRun = async (codes) => {
    setLoading(true);
    setPlan(null);
    try {
      const r = await axios.post(`${API}/admin/migrations/prune-sub-distributors`, null, {
        params: { keep_codes: codes, dry_run: true },
      });
      setPlan(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/migrations/prune-sub-distributors`, null, {
        params: { keep_codes: keep, dry_run: false },
      });
      toast.success(`Promoted ${r.data.totals.promote}, demoted ${r.data.totals.demote}.`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-md border p-4 bg-slate-50 space-y-2">
      <div className="flex items-center gap-2">
        <PlayCircle className="h-4 w-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Prune Sub Distributors</h4>
      </div>
      <p className="text-xs text-slate-600">
        Keep only the whitelisted customer codes as Sub Distributor. Everything
        else in <code>customer_type='iptv_distributor'</code> is demoted to LCO.
      </p>
      <Button size="sm" onClick={openDialog} data-testid="btn-prune-sub-distributors">
        Preview & Run
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Prune Sub Distributors</DialogTitle>
            <DialogDescription>
              Preview the exact rows that will change, then click <b>Apply</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="keep-codes">Keep as Sub Distributor (customer codes, comma-separated)</Label>
              <Input
                id="keep-codes"
                value={keep}
                onChange={(e) => setKeep(e.target.value)}
                onBlur={() => runDryRun(keep)}
                data-testid="prune-keep-codes"
              />
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}
            {plan && (
              <div className="rounded border bg-white text-sm p-3 space-y-2 max-h-72 overflow-auto">
                {plan.keep_codes_missing?.length > 0 && (
                  <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    <AlertTriangle className="h-4 w-4 mt-0.5" />
                    <p className="text-xs">
                      Missing customer codes (not in DB): <b>{plan.keep_codes_missing.join(', ')}</b>.
                      Check spelling before applying.
                    </p>
                  </div>
                )}
                <p>
                  <b>Will promote to Sub Distributor:</b>{' '}
                  {plan.totals?.promote > 0 ? plan.will_promote_to_sub.join(', ') : '—'}
                </p>
                <p>
                  <b>Will demote to LCO:</b> {plan.totals?.demote || 0} row(s)
                </p>
                {plan.will_demote_to_lco?.length > 0 && (
                  <ul className="ml-3 list-disc text-xs text-slate-600 space-y-0.5">
                    {plan.will_demote_to_lco.slice(0, 30).map((r) => (
                      <li key={r.customer_id}>
                        {r.customer_code || r.customer_id} — {r.company_name || '—'}
                      </li>
                    ))}
                    {plan.will_demote_to_lco.length > 30 && (
                      <li className="italic">…and {plan.will_demote_to_lco.length - 30} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => runDryRun(keep)} disabled={loading}>
              Refresh preview
            </Button>
            <Button
              onClick={apply}
              disabled={applying || loading || !plan || (plan.totals?.promote === 0 && plan.totals?.demote === 0)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="prune-apply-btn"
            >
              {applying ? 'Applying…' : 'Apply migration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignUnassignedLcos({ API }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('ANT-P2026040653');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    await runDryRun(target);
  };

  const runDryRun = async (code) => {
    setLoading(true);
    setPlan(null);
    try {
      const r = await axios.post(`${API}/admin/migrations/assign-unassigned-lcos`, null, {
        params: { target_code: code, dry_run: true },
      });
      setPlan(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/migrations/assign-unassigned-lcos`, null, {
        params: { target_code: target, dry_run: false },
      });
      toast.success(`Attached ${r.data.applied_count || r.data.totals.assigned} LCO(s) to ${target}.`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-md border p-4 bg-slate-50 space-y-2">
      <div className="flex items-center gap-2">
        <PlayCircle className="h-4 w-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Assign Unassigned LCOs</h4>
      </div>
      <p className="text-xs text-slate-600">
        Every LCO with no <code>parent_distributor_id</code> is attached to the target Sub Distributor.
        Existing <code>parent_partner_id</code> is preserved.
      </p>
      <Button size="sm" onClick={openDialog} data-testid="btn-assign-unassigned-lcos">
        Preview & Run
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Unassigned LCOs</DialogTitle>
            <DialogDescription>
              Preview the LCOs that will be attached, then click <b>Apply</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="assign-target">Target Sub Distributor (customer code)</Label>
              <Input
                id="assign-target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                onBlur={() => runDryRun(target)}
                data-testid="assign-target-code"
              />
            </div>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}
            {plan && (
              <div className="rounded border bg-white text-sm p-3 space-y-2 max-h-72 overflow-auto">
                {plan.error && (
                  <div className="flex items-start gap-2 text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">
                    <AlertTriangle className="h-4 w-4 mt-0.5" />
                    <p className="text-xs">{plan.error}</p>
                  </div>
                )}
                {plan.target && (
                  <p><b>Target:</b> {plan.target.customer_code} — {plan.target.company_name || '—'} ({plan.target.current_type})</p>
                )}
                {plan.target_type_warning && (
                  <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 text-xs">
                    {plan.target_type_warning}
                  </p>
                )}
                <p><b>Will attach:</b> {plan.totals?.assigned || 0} LCO(s)</p>
                {plan.will_assign?.length > 0 && (
                  <ul className="ml-3 list-disc text-xs text-slate-600 space-y-0.5">
                    {plan.will_assign.slice(0, 30).map((r) => (
                      <li key={r.customer_id}>
                        {r.customer_code || r.customer_id} — {r.company_name || '—'}
                      </li>
                    ))}
                    {plan.will_assign.length > 30 && (
                      <li className="italic">…and {plan.will_assign.length - 30} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => runDryRun(target)} disabled={loading}>
              Refresh preview
            </Button>
            <Button
              onClick={apply}
              disabled={applying || loading || !plan || plan.error || (plan.totals?.assigned || 0) === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="assign-apply-btn"
            >
              {applying ? 'Applying…' : 'Apply migration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
